<template>
  <div
    class="sale-box"
    :class="{ 'sale-box--in-basket': productDetails.product.inBasket }"
    role="region"
    :aria-label="productDetails.product.name"
  >
    <slot name="schema-org" />
    <div
      class="sale-box__form"
      id="article-form"
    >
      <div
        v-if="productStore.upsellQuantity && isBoxSetLayer()"
      >
        <lightbox
          :initial-opened="true"
        >
          <div class="sale-box__upsell-quantity">
            <div class="sale-box__upsell-quantity__headline">
              <image-element
                :src="[settings.flags.src.boxSetImage]"
                alt=""
              />
              {{ $t('ads.ads_top_info.upsellQuantity.headline') }}
            </div>
            <div
              class="sale-box__upsell-quantity__content"
              v-html="upsellQuantityContent()"
            />
            <div class="lightbox__buttons">
              <button
                class="button button--link"
                @click="closeLightBox()"
                v-html="$t('ads.ads_top_info.upsellQuantity.button.no')"
              />
              <button
                class="button button--primary"
                @click="addQuantityToBasket(productStore.upsellQuantity); closeLightBox();"
                v-html="$t('ads.ads_top_info.upsellQuantity.button.yes')"
              />
            </div>
          </div>
        </lightbox>
      </div>
      <div
        class="sale-box__basket-header"
        v-if="productDetails.product.inBasket"
      >
        {{ $t('ads.ads_top_info.article_in_basket') }}
      </div>
      <div
        class="sale-box__basket-header"
        v-if="productDetails.product.successorInBasket && !productDetails.product.inBasket && addSuccessorActive()"
      >
        {{ $t('ads.ads_top_info.successor_in_basket') }}
      </div>
      <div
        class="sale-box__basket-header-subline"
        v-if="productDetails.product.deliveredInBox"
      >
        {{ $t('ads.ads_top_info.boxSetLayer.article_in_basket') }}
      </div>
      <div
        class="sale-box__savings-header"
        v-if="productDetails.product.bulkPrice && !productDetails.product.inBasket"
      >
        <span class="sale-box__savings-header__text">
          {{ $t(`product.bulkPrice.volumeUnit.${productDetails.product.bulkPrice.volumeUnit}`, [productDetails.product.bulkPrice.quantity]) }}
          <span class="sale-box__savings-header__text-bold">
            {{ productDetails.product.bulkPrice.price }}
          </span>
          <span
            class="sale-box__savings-header__text-small"
            v-if="productDetails.product.bulkPrice.unitPrice"
          >
            {{ $t(`product.price.volumeUnit.${productDetails.product.bulkPrice.volumeUnit}`) }} ({{ productDetails.product.bulkPrice.unitPrice }}/{{ productDetails.product.bulkPrice.unit }})
          </span>
        </span>
      </div>
      <div class="sale-box__inputs-hidden">
        <csrf-input
          ref="csrf"
        />
      </div>
      <sale-box-segment-head
        :min-reco-percentage="minRecoPercentage"
        :product-details="productDetails"
        :state="state"
        @wishlistClicked="toggleWishlist"
        @rateNowClicked="handleRateNowClicked"
        :product-has-current-users-review="productHasCurrentUsersReview"
      />
      <div
        v-if="getPromotionText()"
        class="sale-box__promotion"
        v-html="getPromotionText()"
      />
      <div
        class="sale-box__selling-points"
        v-if="productDetails.product.sellingPoints"
        v-html="productDetails.product.sellingPoints"
      />

      <product-quickfacts v-if="quickfactsShown" />

      <div
        class="sale-box__frequency-box frequency-box"
        v-if="hasRecurringOrderFrequencies"
      >
        <button
          v-for="frequency in recurringOrderFrequencies"
          :key="frequency"
          class="frequency-box__option"
          :class="{
            'frequency-box__option--active': frequency === selectedRecurringOrderFrequency
          }"
          @click="changeFrequency(frequency)"
        >
          <span>{{ $t(`ads.sparabo.frequencyBox.${frequency}`) }}</span>
        </button>
      </div>

      <div
        class="sale-box__price-wrapper"
        v-if="isPriceInfoVisible()"
      >
        <price-info
          v-if="!isOnRequestWithoutPrice(productDetails.product.purchaseOnRequestArticle)"
          :value="productDetails.product.price"
          :show-tax="true"
          :show-shipping-included="productDetails.product.deliveryCostIncluded"
          :shipping-info-url="productDetails.product.shippingInfoUrl"
          :with-savings-max="withSavingsMax || productDetails.pageType === 'AQ_LANDING_PAGE'"
        />
        <div class="sale-box__article-number">
          {{ $t('price.info.articleId') }} {{ productDetails.product.number }}
        </div>
      </div>
      <div
        class="sale-box__product-variations"
        :class="{
          'sale-box__product-variations--loading': state === 'loadingVariation'
        }"
        v-if="productVariation.attributeNames.length > 0 && productVariation.items.length > 1"
      >
        <product-variation-input
          class="sale-box__variations"
          :initial-selected-item-id="productDetails.product.itemId"
          :variations="productVariation"
          :hide-selected-item="true"
          @changeVariation="productUpdateHandler"
          ref="productVariation"
        />
      </div>
      <div
        class="sale-box__buttons"
        :class="{
          'sale-box__buttons--availability-reminder': productDetails.product.showAvailabilityReminder ,
          'sale-box__buttons--alternatives-button': showSoldoutAlternativesButton()
        }"
      >
        <availability-reminder
          v-if="productDetails.product.showAvailabilityReminder"
          :logged-in="loggedIn"
          :login-page-url="loginPageUrl"
          :subscribed="productDetails.product.availabilityReminderState"
          :item-id="productDetails.product.itemId"
        />
        <template
          v-if="purchaseOnRequestArticle"
        >
          <button
            class="sale-box__button-to-cart | button button--primary"
            type="button"
            @click="openOnRequestLightBox"
          >
            {{ $t('lightbox.purchaseOnRequestArticle.text') }}
          </button>
        </template>
        <template
          v-if="!productDetails.product.zeroPriceItem && productDetails.product.recurringOrderFollowUpPacket"
        >
          <div class="sale-box__buttons">
            <div class="product-box__status product-box__status--unavailable">
              {{ $t('product.button.recurringOrderFollowUpPacket') }}
            </div>
          </div>
        </template>
        <template
          v-else
        >
          <div
            class="sale-box__add-to-basket-html-replacement html-block"
            v-if="productDetails.addToBasketHtmlReplacement"
            v-html="productDetails.addToBasketHtmlReplacement"
          />
          <div
            class="sale-box__qty-wrapper"
            v-if="productDetails.product.availability.available && !productDetails.addToBasketHtmlReplacement"
          >
            <counter-input
              class="sale-box__counter"
              :counter="productDetails.product.counter"
              @update="counterChangeHandler"
              ref="quantity"
            />
          </div>
          <button
            class="sale-box__button-to-cart | button button--primary"
            :class="{
              'button--loading': state === 'loadingBasket',
              'button--disabled': isButtonDisabled()
            }"
            v-if="productDetails.product.availability.available && !productDetails.addToBasketHtmlReplacement"
            @disabled="isButtonDisabled()"
            id="add_to_cart"
            type="button"
            @click="addToBasket"
            name="add_to_cart"
          >
            {{ $t('ads.ads_top_info.add_to_basket') }}
          </button>
          <template v-if="productDetails.product.soldoutRelation">
            <template v-if="productDetails.product.soldoutRelation.type === 'SUCCESSOR' && addSuccessorActive()">
              <counter-input
                class="sale-box__counter"
                :counter="productDetails.product.counter"
                @update="counterChangeHandler"
                ref="quantity"
              />
              <button
                class="sale-box__button-to-cart | button button--successor-to-basket"
                type="button"
                @click="addToBasket"
                name="to_successor"
              >
                {{ $t('availability.available.successor.addToBasket') }}
              </button>
            </template>
            <button
              class="sale-box__button-to-cart | button button--primary"
              v-else-if="productDetails.product.soldoutRelation.type === 'SUCCESSOR'"
              id="to_successor"
              type="button"
              @click="changeSuccessorHandler(productDetails.product.availability.successorItemId)"
              name="to_successor"
            >
              {{ $t('ads.ads_top_info.go_to.' + productDetails.product.soldoutRelation.type) }}
            </button>
            <a
              v-else-if="productDetails.product.soldoutRelation.type === 'ALTERNATIVE'"
              class="sale-box__button-to-cart | button button--primary"
              :href="productDetails.product.soldoutRelation.url"
            >
              {{ $t('ads.ads_top_info.go_to.' + productDetails.product.soldoutRelation.type) }}
            </a>
          </template>
        </template>
      </div>
      <div
        v-if="showSoldoutAlternativesButton()"
        class="sale-box__scroll-button | button button--primary"
        v-text="$t('ads.main.recommendation.salebox.button')"
        @click="scrollToAlternatives()"
      />
      <div class="sale-box__availability-wrapper">
        <a
          :href="`#${$t('ads.main.head.features')}`"
          class="product-detailed-page__info-link"
          v-html="$t('detail.product.characteristics')"
        />
        <div
          class="sale-box__availability-info"
          v-if="(!purchaseOnRequestArticle || productDetails.product.alternativeItemUrl) && !productDetails.product.recurringOrderFollowUpPacket"
        >
          <availability-info
            v-if="!purchaseOnRequestArticle"
            :availability="productDetails.product.availability"
          />
          <div
            class="sale-box__alternative"
            v-if="productDetails.product.alternativeItemUrl"
          >
            <a
              :href="productDetails.product.alternativeItemUrl"
            >
              {{ $t('ads.ads_top_info.go_to.ALTERNATIVE') }}
            </a>
          </div>
        </div>
      </div>
      <div
        class="sale-box__delivery-promise"
        v-if="productDetails.product.deliveryPromise"
        v-html="productDetails.product.deliveryPromise"
      />
    </div>
  </div>
</template>

<script>
  import pigeon from '../../../bps/utilities/js/pigeon/pigeon.js';
  import Ratings from '../../../bps/components/ratings/ratings.vue';
  import ProductVariationInput from '../../../bps/components/productVariationInput/productVariationInput.vue';
  import AvailabilityInfo from '../../../bps/components/availabilityInfo/availabilityInfo.vue';
  import CsrfInput from '../../../bps/components/csrfInput/csrfInput.vue';
  import productStore from '../../../bps/stores/product/ProductStore';
  import productPageService from '../../../bps/services/product/ProductPageService';
  import AvailabilityReminder from '../../../bps/components/availabilityReminder/availabilityReminder';
  import {isOnRequestWithoutPrice} from '../../../bps/utilities/js/onRequestType/OnRequestType';
  import ImageElement from '../../../bps/components/imageElement/imageElement';
  import diContainer from '../../../bps/utilities/js/diContainer/diContainer';
  import jsGlobalConfig from '../../../bps/globals/jsGlobalConfig';
  import {usePlaceholder} from '../../../bps/utilities/vueMixins/placeholderMixin/placeholder';
  import * as scrollToElement from 'scroll-to-element';
  import Quickfacts from '@/bps/components/quickfacts/quickfacts.vue';
  import ProductQuickfacts from '@/bps/components/productQuickFacts/productQuickfacts.vue';


  const SaleBox = {
    name: 'sale-box',
    components: {
      ProductQuickfacts,
      Quickfacts,
      ImageElement,
      Ratings,
      CsrfInput,
      ProductVariationInput,
      AvailabilityInfo,
      AvailabilityReminder
    },
    props: {
      trackingSkipVariationChange: Boolean,
      loggedIn: Boolean,
      loginPageUrl: String,
      recurringOrder: Boolean,
      withSavingsMax: Boolean,
      productHasCurrentUsersReview: Boolean,
      quickfactsShown: Boolean
    },
    data() {
      return {
        productStore: productStore,
        settings: diContainer.get('productDetailsSettings'),
        innerQuantity: productStore.productDetails.product.counter.preselectedQuantity,
        state: '',
        minRecoPercentage: 75,
        isOnRequestWithoutPrice: isOnRequestWithoutPrice
      };
    },
    mounted() {
      usePlaceholder('sale-box');
    },
    computed: {
      productDetails() {
        return this.productStore.productDetails;
      },
      productVariation() {
        return this.productStore.productVariation;
      },
      selectedQty() {
        return productStore.selectedQty;
      },
      recurringOrderFrequencies() {
        return productStore.recurringOrderFrequencies;
      },
      selectedRecurringOrderFrequency() {
        return productStore.selectedRecurringOrderFrequency;
      },
      hasRecurringOrderFrequencies() {
        return this.selectedRecurringOrderFrequency && this.selectedRecurringOrderFrequency !== 'UNKNOWN';
      },
      purchaseOnRequestArticle() {
        return this.productStore.productDetails.product.purchaseOnRequestArticle;
      }
    },
    methods: {
      counterChangeHandler(value) {
        this.innerQuantity = value;
        productStore.selectedQty = value;
        productPageService.changeQuantity(this.productDetails.product.itemId, value, this.trackingSkipVariationChange);
      },
      addToBasket() {
        this.state = 'loadingBasket';
        productPageService.addToBasketFromStore(this.innerQuantity, this.selectedRecurringOrderFrequency).then(() => {
          this.state = '';
        });
      },
      addQuantityToBasket(quantity) {
        this.state = 'loadingBasket';
        productPageService.addToBasketFromStore(quantity, this.selectedRecurringOrderFrequency).then(() => {
          this.state = '';
        });
      },
      upsellQuantityContent() {
        if (productStore.upsellQuantity === 1) {
          return this.$t('ads.ads_top_info.upsellQuantity.content.one', [productStore.upsellQuantity]);
        }
        return this.$t('ads.ads_top_info.upsellQuantity.content.more', [productStore.upsellQuantity]);
      },
      isBoxSetLayer() {
        return this.settings.flags.whitelist && this.settings.flags.whitelist.indexOf('boxSetLayer') >= 0;
      },
      isButtonDisabled() {
        return this.productDetails.product.recurringOrderFollowUpPacket;
      },
      toggleWishlist() {
        this.state = 'loadingWishlist';
        if (this.productDetails.product.inWishlist) {
          productPageService.removeFromWishlist(this.productDetails.product.id, this.productDetails.product.itemId).then((response) => {
            this.state = '';
          });
        } else {
          productPageService.addToWishlistFromStore(this.productDetails.product.id, this.productDetails.product.itemId).then((response) => {
            this.state = '';
          });
        }
      },
      productUpdateHandler(itemId) {
        this.state = 'loadingVariation';
        productPageService.changeVariation(itemId, this.innerQuantity, this.trackingSkipVariationChange).then(() => {
          this.state = '';
          if (!this.addSuccessorActive() && this.settings.referredByHash && window.location.href.indexOf(this.settings.referredByHash) >= 0) {
            history.replaceState(undefined, undefined, '#');
          }
        });
      },
      changeSuccessorHandler(itemId) {
        this.state = 'loadingVariation';
        const previouslyWban = this.productStore.trackingData.wban;
        productPageService.changeVariation(itemId, this.innerQuantity, this.trackingSkipVariationChange).then(() => {
          this.state = '';
          if (!this.addSuccessorActive() && this.settings.referredByHash) {
            history.replaceState(undefined, undefined, this.settings.referredByHash + previouslyWban);
          }
        });
      },
      openOnRequestLightBox() {
        pigeon.publish('lightbox:showUrl', window.__globalConfig.onRequestLightBoxUrl);
      },
      getPromotionText() {
        return this.productDetails.flags.takeAndPayBenefitAppliedItemsText ||
          this.productDetails.flags.takeAndPayBenefitText ||
          '';
      },
      changeFrequency(newFrequency) {
        productStore.selectedRecurringOrderFrequency = newFrequency;
      },
      addSuccessorActive() {
        return jsGlobalConfig().addSuccessorToBasket();
      },
      closeLightBox() {
        pigeon.publish('lightbox:close');
        productStore.upsellQuantity = null;
      },
      handleRateNowClicked() {
        pigeon.publish('productReview:showForm');
      },
      showSoldoutAlternativesButton() {
        return jsGlobalConfig().getShowSoldoutAlternatives() &&
          !this.productDetails.product.availability.subscriptionPreview &&
          this.productDetails.product.availability.status === 'NOT_AVAILABLE' &&
          this.productStore.productAlternativesLength > 0;
      },
      scrollToAlternatives() {
        const el = document.querySelector('.product-alternatives');
        scrollToElement(el, {offset: -90, duration: 400});
      },
      isPriceInfoVisible() {
        if (this.productDetails.product.recurringOrderFollowUpPacket && !this.productDetails.product.zeroPriceItem) {
          return this.productDetails.product.recurringOrderFollowUpPriceEnabled;
        }
        return (
          !this.recurringOrder ||
          (this.recurringOrder && this.productDetails.product.availability.available)
        ) && !this.productDetails.product.zeroPriceItem
          && !this.productDetails.product.availability.subscriptionPreview;
      },
      partnerUrl() {
        if (jsGlobalConfig().getPartnerUrl() && this.productDetails.product.assortmentSupplierDeliveryMessage) {
          return `${jsGlobalConfig().getPartnerUrl()}${this.productStore.productDetails.product.artNr}`;
        }
        return null;
      },
      openAssortmentSupplierLightbox() {
        pigeon.publish('lightbox:showUrl', this.$t('lightbox.partner.url'));
      }
    },
    watch: {
      selectedQty() {
        this.innerQuantity = productStore.selectedQty;
        this.$refs.quantity.setValue(productStore.selectedQty);
      }
    }
  };

  export default SaleBox;
</script>
